import { Link } from "react-router-dom";
import { HeartIcon } from "@heroicons/react/24/outline";
import { useGetActiveProfileDefinition } from "../../../../api/queries";
import {
  SectionEmptyState,
  SectionHeader,
} from "../../../../componentLibrary/SectionPanel";
import { pluralize } from "../../../../utils/pluralize";
import type { ModuleI } from "../../../../types";

interface Props {
  topic: ModuleI;
  topicId: string;
}

export default function ProfileDefinitionSection({ topic, topicId }: Props) {
  const { data: activeProfileDefinition } = useGetActiveProfileDefinition(
    topic._id,
  );
  const title =
    topic.slug === "anxiety" ? "About The Anxiety" : `About My ${topic.title}`;
  const href = `/admin/toolboxes/${topicId}/profile-definitions`;

  if (activeProfileDefinition) {
    return (
      <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white p-4">
        <SectionHeader
          icon={<HeartIcon className="w-5 h-5 text-electrum-300 shrink-0" />}
          title={title}
          action="Edit"
          actionTo={`${href}/${activeProfileDefinition._id}/edit`}
        />
        <div className="space-y-2 py-1">
          <p className="text-xs font-semibold text-gray-400 tracking-wide">
            {activeProfileDefinition.displayName?.trim() ||
              `v${activeProfileDefinition.version}`}{" "}
            ·{" "}
            {pluralize(
              "question",
              activeProfileDefinition.questions.length,
              true,
            )}
          </p>
          {activeProfileDefinition.questions.length > 0 ? (
            <ul className="space-y-1">
              {activeProfileDefinition.questions.map((q) => (
                <li key={q.questionId} className="flex items-start gap-1.5">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-300 shrink-0" />
                  <span className="text-sm text-gray-700">
                    {q.content?.label ?? q.type}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">No questions yet</p>
          )}
          <Link
            to={href}
            className="inline-block pt-1 text-xs font-medium text-electrum-300 hover:text-electrum transition-colors"
          >
            Manage versions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={href}
      className="block rounded-xl border-2 border-dashed border-gray-300 bg-white p-4 hover:border-electrum-300 hover:bg-electrum-50 transition-colors group"
    >
      <SectionHeader
        icon={<HeartIcon className="w-5 h-5 text-electrum-300 shrink-0" />}
        title={title}
      />
      <SectionEmptyState>
        <p className="text-sm text-gray-400 py-2 group-hover:text-electrum-300 transition-colors">
          No active version — click to manage profile definitions
        </p>
      </SectionEmptyState>
    </Link>
  );
}
